import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, FileSearch, ClipboardCheck, Settings, Wind, Activity, BookOpen, ArrowRight } from 'lucide-react';

const About = () => {
  // SEO handled by centralized SEO component in App.js
  const services = [
    {
      title: 'Permit Feasibility & Strategy',
      description: 'Pre-project Potential to Emit (PTE) calculations and \'fatal flaw\' analysis to determine if a project triggers Title V or PSD review.',
      icon: <FileSearch className="w-6 h-6" />,
      link: '/permit-feasibility',
      borderColor: 'border-violet-500',
      iconColor: 'text-violet-600',
      linkColor: 'text-violet-700',
    },
    {
      title: 'Air Permit Applications',
      description: 'Expert preparation of Title V, Minor Source (18 AAC 50), and General Permits (MG) tailored to Alaska DEC standards.',
      icon: <ClipboardCheck className="w-6 h-6" />,
      link: '/services/permit-applications',
      borderColor: 'border-emerald-500',
      iconColor: 'text-emerald-600',
      linkColor: 'text-emerald-700',
    },
    {
      title: 'Equipment Selection',
      description: 'Capital-efficient guidance on selecting engines and boilers that meet EPA Tier standards and NSPS requirements.',
      icon: <Settings className="w-6 h-6" />,
      link: '/equipment-selection',
      borderColor: 'border-amber-500',
      iconColor: 'text-amber-600',
      linkColor: 'text-amber-700',
    },
    {
      title: 'BACT & Emissions Controls',
      description: 'Evaluation of Best Available Control Technologies (BACT) and stack testing coordination to ensure compliance limits are met.',
      icon: <Wind className="w-6 h-6" />,
      link: '/pollution-control',
      borderColor: 'border-cyan-500',
      iconColor: 'text-cyan-600',
      linkColor: 'text-cyan-700',
    },
    {
      title: 'Compliance & Reporting',
      description: 'Management of Triennial Emissions Inventories, GHG Mandatory Reporting, and Facility Operating Reports (FOR).',
      icon: <Activity className="w-6 h-6" />,
      link: '/services/compliance-support',
      borderColor: 'border-blue-600',
      iconColor: 'text-blue-600',
      linkColor: 'text-blue-700',
    },
    {
      title: 'Regulatory Guidance',
      description: 'Technical representation during ADEC and EPA Region 10 negotiations to ensure practical permit conditions.',
      icon: <BookOpen className="w-6 h-6" />,
      link: '/regulatory-support',
      borderColor: 'border-slate-600',
      iconColor: 'text-slate-600',
      linkColor: 'text-slate-700',
    },
  ];

  const achievements = [
    'PSD Avoidance & BACT Strategy: Strategic review of Major Source determinations to avoid complex PSD permitting where possible.',
    'Source Test Oversight: Technical management of compliance testing protocols and validation of emission results.',
    "Alaska SIP Development: Technical stakeholder representation during the drafting of Alaska's State Implementation Plan.",
    'GHG Permitting Frameworks: Development of practical permit conditions for Greenhouse Gas (40 CFR Part 98) compliance.',
    'General Permit Authorization: Expedited approval of MG1/MG2 permits for portable oil & gas and rock crushing facilities.',
    'Capital Cost Reduction: Implementation of permitting strategies that reduced operational compliance costs by over 30%.',
    'Regulatory Advocacy: Technical comment submission on proposed ADEC and EPA regulatory changes.'
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section
        className="relative bg-gradient-to-r from-emerald-900 to-emerald-700 text-white py-20"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.unsplash.com/photo-1640125346217-e4bb9313d6bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjBmb3Jlc3R8ZW58MHx8fHwxNzYxMTAxMTkxfDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="page-title">
            About Raven Environmental: Your Trusted Environmental Consulting Partner in Alaska
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl">
            Raven Environmental is the premier environmental consulting firm in Alaska. Our mission is to provide cost-effective, expert assistance in obtaining air quality permitting and compliance for stationary sources across the state.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-28 bg-white" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              Navigating Alaska Environmental Compliance with Expert Consulting
            </h2>

            {/* Content with improved spacing */}
            <div className="space-y-8 text-gray-600 leading-relaxed text-lg">
              <p>
                Navigating the complexities of environmental permitting can be challenging. That's where we come in. Our experienced team is equipped to guide you through every step of the process, ensuring a smooth and efficient permitting experience.
              </p>
              <p>
                We prioritize not only compliance but also the promotion of your business objectives, ensuring that your operations remain economically viable while maintaining a commitment to environmental integrity.
              </p>
              <p>
                With our deep understanding of local and federal regulations, we work diligently to develop tailored strategies that meet your specific needs. Our goal is to simplify the permitting process, allowing you to focus on what you do best—running your business.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-16 text-center">
            Our Specialized Environmental Consulting Services in Alaska
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {services.map((service, index) => {
              const ServiceCard = service.link ? Link : 'div';
              return (
                <ServiceCard
                  key={index}
                  to={service.link || undefined}
                  data-testid={`service-${index}`}
                  className={`group bg-white p-10 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border-t-4 ${service.borderColor} hover:-translate-y-1 ${service.link ? 'cursor-pointer' : ''}`}
                >
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${service.iconColor} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 transition-colors">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                  {service.link && (
                    <div className="flex items-center font-semibold hover:opacity-80 transition-opacity">
                      <span className={service.linkColor}>Learn More</span>
                      <ArrowRight className={`ml-2 group-hover:translate-x-1 transition-transform duration-300 ${service.linkColor}`} size={18} />
                    </div>
                  )}
                </ServiceCard>
              );
            })}
          </div>
          
          {/* Secondary CTA Button */}
          <div className="mt-20 text-center">
            <Link
              to="/contact"
              data-testid="secondary-cta"
              className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-emerald-700 to-emerald-600 text-white text-lg rounded-xl font-bold hover:from-emerald-800 hover:to-emerald-700 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1"
            >
              Ready to Start Your Project?
              <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" size={22} />
            </Link>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50" data-testid="achievements-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            What We've Achieved
          </h2>
          <p className="text-xl text-gray-600 mb-16 text-center max-w-3xl mx-auto">
            Our track record of environmental excellence and regulatory expertise across Alaska
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {achievements.map((achievement, index) => (
              <div
                key={index}
                data-testid={`achievement-${index}`}
                className="group relative bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                {/* Gradient accent bar */}
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600"></div>
                
                <div className="flex items-start space-x-5 p-8">
                  {/* Icon container with animated background */}
                  <div className="flex-shrink-0 relative">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow duration-300 group-hover:scale-110 transform transition-transform">
                      <CheckCircle className="w-7 h-7 text-emerald-600" strokeWidth={2.5} />
                    </div>
                    {/* Subtle glow effect on hover */}
                    <div className="absolute inset-0 bg-emerald-400 rounded-xl opacity-0 group-hover:opacity-20 blur-md transition-opacity duration-300"></div>
                  </div>
                  
                  {/* Achievement text */}
                  <div className="flex-1 pt-1">
                    <p className="text-gray-700 leading-relaxed text-base font-medium group-hover:text-gray-900 transition-colors duration-200">
                      {achievement}
                    </p>
                  </div>
                </div>
                
                {/* Subtle corner accent */}
                <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-br from-transparent to-emerald-50 opacity-30 rounded-tl-full"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Button Section */}
      <section className="py-12 bg-white" data-testid="cta-button-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/industries"
            data-testid="cta-get-started"
            className="inline-flex items-center px-8 py-4 bg-emerald-700 text-white rounded-lg font-semibold hover:bg-emerald-800 transition-colors shadow-lg"
          >
            Get Started
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;